import { PLANS_TARGET } from '@/domain/assistant/assistant-nav-targets';
import type { IGhCalloutCard } from '../gpthelp-host-contract';
import { clampText, joinSentences } from './card-text';
import type { ICardFormat } from './card-text';

export interface IPermissionLockInput {
  id: string;
  /** i18n key of the title (`assistant-locks.ts` `permissionLockKey`). */
  titleKey: string;
  /** The member can still turn products on and off: say so. */
  canToggle: boolean;
  /**
   * What they can turn on and off, when `canToggle` (`lockToggleKey`:
   * categories for a category op); default products.
   */
  toggleKey?: string;
}

export interface IPlanLockInput {
  id: string;
  title: string;
  /** May be empty; `planAsk` follows as its own paragraph when the member cannot change the plan. */
  text: string;
  /** StoreSettings: the member may open the plans («Ver planes»). */
  canSeePlans: boolean;
}

/** The plan name a tier needs (Plus = 1, Pro = 2). */
export function planName(minTier: number): string {
  return minTier >= 2 ? 'Pro' : 'Plus';
}

/**
 * «Su usuario no puede cambiar precios.»: what this member's role does not
 * allow, whom to ask, and (when true) that hiding products, or categories,
 * is allowed.
 */
export function permissionLockCard(
  input: IPermissionLockInput,
  fmt: ICardFormat
): IGhCalloutCard {
  return {
    id: input.id,
    v: 1,
    type: 'callout',
    tone: 'lock',
    icon: 'lock',
    title: clampText(fmt.t(input.titleKey), 160),
    text: clampText(
      joinSentences(fmt.locale, [
        fmt.t('assistant.lock.catalogHint'),
        input.canToggle
          ? fmt.t(input.toggleKey ?? 'assistant.lock.canToggle')
          : '',
      ]),
      600
    ),
  };
}

/**
 * Something the store's plan does not include, with «Ver planes» for the
 * members who may change the plan; the others are told to ask the owner.
 */
export function planLockCard(
  input: IPlanLockInput,
  fmt: ICardFormat
): IGhCalloutCard {
  const paragraphs = input.canSeePlans
    ? [input.text]
    : [input.text, fmt.t('assistant.lock.planAsk')];
  const text = clampText(
    paragraphs.filter((part) => part !== '').join('\n\n'),
    600
  );
  const card: IGhCalloutCard = {
    id: input.id,
    v: 1,
    type: 'callout',
    tone: 'lock',
    icon: 'crown',
    title: clampText(input.title, 160),
  };
  if (text) card.text = text;
  if (input.canSeePlans)
    card.actions = [
      {
        id: 'plans',
        kind: 'navigate',
        label: clampText(fmt.t('assistant.lock.seePlans'), 40),
        style: 'outline',
        target: { page: PLANS_TARGET },
      },
    ];
  return card;
}
